import { Link } from "react-router-dom";
import { Menu } from "lucide-react";
import { useState } from "react";

function Nav() {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 border-b border-[#e3ddd2]">
      <div className="max-w-6xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="privacy-brand">
          <img src="/brand/logo-black.png" alt="ourweddly" className="h-10 sm:h-12 w-auto" />
        </Link>
        <div className="hidden md:flex items-center gap-3">
          <Link to="/login" className="text-sm text-neutral-600 hover:text-[#1a1a1a] transition-colors px-3">Accedi</Link>
          <Link to="/register" className="btn-wed !py-2 !px-5 text-sm">Inizia gratis</Link>
        </div>
        <button className="md:hidden p-2" onClick={() => setOpen((o) => !o)}><Menu size={22} /></button>
      </div>
      {open && (
        <div className="md:hidden border-t border-[#e3ddd2] bg-white px-6 py-4 flex flex-col gap-3 text-sm">
          <Link to="/login" onClick={() => setOpen(false)}>Accedi</Link>
          <Link to="/register" className="btn-wed !py-2 text-center" onClick={() => setOpen(false)}>Inizia gratis</Link>
        </div>
      )}
    </header>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-10">
      <h2 className="font-wed text-xl sm:text-2xl mb-3 text-[#1a1a1a]">{title}</h2>
      <div className="text-neutral-600 leading-relaxed space-y-3">{children}</div>
    </section>
  );
}

export default function Privacy() {
  return (
    <div className="grain min-h-screen" data-testid="privacy-page">
      <Nav />

      <main className="max-w-3xl mx-auto px-6 sm:px-8 py-16 sm:py-20">
        <p className="eyebrow mb-4">Legale</p>
        <h1 className="font-wed text-3xl sm:text-4xl tracking-tight mb-3">Informativa sulla privacy</h1>
        <p className="text-sm text-neutral-400 mb-12">Ultimo aggiornamento: 14 ottobre 2026</p>

        <Section title="1. Chi siamo">
          <p>
            ourweddly ("noi", "il Servizio") è una piattaforma che permette di creare una pagina web per il proprio
            matrimonio: pagina pubblica, conferme di presenza (RSVP), piano tavoli e galleria foto condivisa con gli
            invitati. Questa informativa spiega quali dati raccogliamo, perché, e come puoi controllarli, in
            conformità al Regolamento (UE) 2016/679 (GDPR).
          </p>
          <p>
            Per qualsiasi richiesta relativa ai tuoi dati personali puoi scriverci a{" "}
            <a href="mailto:privacy@ourweddly.com" className="underline">privacy@ourweddly.com</a>.
          </p>
        </Section>

        <Section title="2. Dati che raccogliamo">
          <p><strong>Account organizzatore.</strong> Email, nome, password (salvata in forma crittografata, mai in chiaro) oppure, se accedi con Google, il tuo nome ed email forniti da Google tramite OAuth — in quel caso non vediamo né salviamo mai la tua password Google.</p>
          <p><strong>Dati del matrimonio.</strong> Nomi degli sposi, data e luogo dell'evento, programma, budget, piano tavoli, storia della coppia e le foto che carichi come organizzatore.</p>
          <p><strong>Dati degli invitati.</strong> Quando un invitato compila il modulo RSVP pubblico: nome, email o telefono (facoltativi), conferma di presenza, numero di accompagnatori, eventuali note alimentari o messaggi. Questi dati sono inseriti dall'invitato stesso o dall'organizzatore e sono visibili solo all'organizzatore e agli eventuali collaboratori dell'evento.</p>
          <p><strong>Foto e video caricati dagli invitati.</strong> Gli invitati possono caricare foto e video nella galleria condivisa senza bisogno di registrarsi. Il nome inserito al momento del caricamento viene associato al file.</p>
          <p><strong>Statistiche di utilizzo.</strong> Per aiutare l'organizzatore a capire come viene usata la pagina, registriamo eventi aggregati e anonimi (es. "la pagina è stata visitata", "sono state scaricate 3 foto", "è stata usata la ricerca tavolo"). Questi eventi <strong>non includono mai l'indirizzo IP, il browser/dispositivo usato, né alcun identificatore che permetta di riconoscere la singola persona</strong> — sono semplici contatori aggregati per evento e per giorno.</p>
          <p><strong>Analisi del traffico.</strong> Usiamo Cloudflare Web Analytics per misurare le visite al sito in forma aggregata. È un servizio senza cookie e senza fingerprinting, pensato per non tracciare i singoli visitatori.</p>
        </Section>

        <Section title="3. Perché usiamo questi dati">
          <ul className="list-disc pl-5 space-y-1">
            <li>Far funzionare il Servizio: creare e mostrare la tua pagina, gestire RSVP, tavoli, programma e galleria.</li>
            <li>Inviarti email transazionali necessarie al Servizio: notifica di una nuova conferma RSVP, invito di un collaboratore, reimpostazione della password. Non inviamo email di marketing.</li>
            <li>Mostrare all'organizzatore statistiche aggregate sull'utilizzo della propria pagina.</li>
            <li>Sicurezza: prevenire abusi e accessi non autorizzati.</li>
          </ul>
        </Section>

        <Section title="4. Dove sono conservati i dati">
          <p>
            I dati (database e file) sono conservati sull'infrastruttura di Cloudflare (D1 per i dati strutturati,
            R2 per foto e video), un fornitore che offre garanzie adeguate di sicurezza e, dove applicabile, di
            trasferimento dei dati conforme al GDPR. Le email transazionali sono inviate tramite Resend.
          </p>
        </Section>

        <Section title="5. Con chi condividiamo i dati">
          <p>
            Non vendiamo né condividiamo i tuoi dati con terzi per finalità pubblicitarie. I dati del tuo matrimonio
            (RSVP, foto, piano tavoli) sono visibili solo a te e agli eventuali collaboratori che inviti. Usiamo un
            numero limitato di fornitori tecnici che trattano dati per nostro conto, solo per far funzionare il
            Servizio:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Cloudflare — hosting, database, storage, protezione e analisi del traffico aggregata.</li>
            <li>Resend — invio delle email transazionali.</li>
            <li>Google — se scegli di accedere con "Accedi con Google".</li>
            <li>Spotify — se usi la funzione di richiesta canzoni, le anteprime musicali vengono mostrate tramite un lettore Spotify incorporato, soggetto all'informativa privacy di Spotify.</li>
          </ul>
        </Section>

        <Section title="6. Per quanto tempo conserviamo i dati">
          <p>
            Conserviamo i dati del tuo matrimonio finché il tuo account resta attivo. Se desideri la cancellazione
            del tuo account, del tuo evento o di dati specifici di un invitato, scrivici a{" "}
            <a href="mailto:privacy@ourweddly.com" className="underline">privacy@ourweddly.com</a> e provvederemo
            entro 30 giorni.
          </p>
        </Section>

        <Section title="7. I tuoi diritti">
          <p>In quanto interessato ai sensi del GDPR, hai diritto di:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Accedere ai dati che abbiamo su di te e ottenerne una copia.</li>
            <li>Chiedere la correzione di dati inesatti.</li>
            <li>Chiedere la cancellazione dei tuoi dati ("diritto all'oblio").</li>
            <li>Opporti o limitare il trattamento in determinati casi.</li>
            <li>Richiedere la portabilità dei tuoi dati.</li>
            <li>Proporre reclamo al Garante per la protezione dei dati personali (garanteprivacy.it).</li>
          </ul>
          <p>Per esercitare questi diritti scrivici a <a href="mailto:privacy@ourweddly.com" className="underline">privacy@ourweddly.com</a>.</p>
        </Section>

        <Section title="8. Cookie e tecnologie simili">
          <p>
            Il Servizio usa il solo spazio di archiviazione del browser (localStorage) necessario per mantenere la
            tua sessione di accesso: non è un cookie di tracciamento e non richiede consenso ai sensi della
            normativa ePrivacy, trattandosi di una funzionalità tecnica essenziale. Non utilizziamo cookie di
            profilazione o pubblicitari.
          </p>
        </Section>

        <Section title="9. Modifiche a questa informativa">
          <p>
            Potremmo aggiornare periodicamente questa informativa per riflettere cambiamenti nel Servizio o nella
            normativa. La data di "ultimo aggiornamento" in cima alla pagina indica l'ultima revisione.
          </p>
        </Section>
      </main>

      <section className="bg-[#1a1a1a] text-white">
        <div className="border-t border-white/10">
          <div className="max-w-6xl mx-auto px-6 sm:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm text-white/50">
            <img src="/brand/logo-white.png" alt="ourweddly" className="h-8 w-auto" />
            <p>© {new Date().getFullYear()} ourweddly. Fatto con cura in Italia.</p>
          </div>
        </div>
      </section>
    </div>
  );
}
